import { ApolloClient, InMemoryCache, createHttpLink, from, Observable, FetchResult } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from "@apollo/client/link/error";
import { authMonitoringService } from './services/authMonitoringService';
import { debugLog } from './utils/debugLog';
import { isMaintenanceError, reportMaintenance } from './utils/maintenance';

// Automatically detect the correct API endpoint based on environment
export const getGraphQLEndpoint = () => {
  // Explicit override (recommended for staging/prod)
  const configuredBaseUrl = process.env.REACT_APP_API_URL;
  if (configuredBaseUrl) {
    return `${configuredBaseUrl.replace(/\/$/, '')}/graphql`;
  }

  // Development or local testing
  if (process.env.NODE_ENV === 'development' || 
      window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1') {
    return 'http://localhost:5000/graphql';
  }
  
  // Production deployment on pithplay.com
  if (window.location.hostname === 'app.pithplay.com') {
    return 'https://api.pithplay.com/graphql';
  }
  
  // Fallback for other production environments - use HTTPS without port
  return `https://${window.location.hostname}/api/graphql`;
};

/**
 * Origin of the API for its REST routes (ZIP and invoice downloads). Derived
 * from the GraphQL endpoint so it follows the same host rules; building it
 * from REACT_APP_API_URL with a localhost fallback sent a production build
 * without that variable to localhost:5000.
 */
export const getApiBaseUrl = () =>
  getGraphQLEndpoint().replace(/\/api\/graphql$/, '').replace(/\/graphql$/, '');

const httpLink = createHttpLink({
  uri: getGraphQLEndpoint(),
});

const authLink = setContext(async (_, { headers }) => {
  // If the request already includes an auth header, do not override it.
  const existingAuthHeader =
    (headers as any)?.authorization || (headers as any)?.Authorization;
  if (existingAuthHeader) {
    return { headers: { ...headers } };
  }

  const getE2EToken = () => {
    if (process.env.NODE_ENV === 'production') return null;
    if (process.env.REACT_APP_E2E_AUTH_BYPASS !== '1') return null;
    try {
      if (window.localStorage.getItem('pithplay_e2e_auth_bypass') !== '1') return null;
    } catch {
      return null;
    }
    return 'e2e-test-token';
  };

  // Get the authentication token from Firebase auth with retry logic
  try {
    const { auth } = await import('./firebaseConfig');
    const user = auth.currentUser;
    
    if (user) {
      try {
        // First attempt: get cached token
        const token = await user.getIdToken(false);
        return {
          headers: {
            ...headers,
            authorization: token ? `Bearer ${token}` : "",
          }
        };
      } catch (tokenError) {
        console.warn('Failed to get cached token, trying fresh token:', tokenError);
        
        try {
          // Second attempt: force refresh token
          const freshToken = await user.getIdToken(true);
          return {
            headers: {
              ...headers,
              authorization: freshToken ? `Bearer ${freshToken}` : "",
            }
          };
        } catch (refreshError) {
          console.error('Failed to refresh token, user may need to re-authenticate:', refreshError);
          // Log token refresh failure
          authMonitoringService.logTokenRefresh(user.email || 'unknown', false, 'force_refresh_failed');
          // Token is completely invalid, user needs to sign in again
          throw new Error('Authentication token expired. Please sign in again.');
        }
      }
    }
  } catch (error) {
    console.warn('Error getting Firebase token for GraphQL request:', error);
  }

  const e2eToken = getE2EToken();
  if (e2eToken) {
    return {
      headers: {
        ...headers,
        authorization: `Bearer ${e2eToken}`,
      }
    };
  }
  
  return {
    headers: {
      ...headers,
      authorization: "",
    }
  };
});

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  // The API's maintenance window (the move to the new database): show the
  // notice, and don't treat it as a sign-in problem.
  if (isMaintenanceError(networkError)) {
    reportMaintenance();
    return;
  }

  if (graphQLErrors) {
    for (const { message, locations, path, extensions } of graphQLErrors) {
      debugLog(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      );
      
      // Check for authentication/authorization errors
      if (extensions?.code === 'UNAUTHENTICATED' || 
          extensions?.code === 'FORBIDDEN' ||
          message.includes('token') ||
          message.includes('auth') ||
          message.includes('unauthorized') ||
          message.includes('expired')) {
        
        if (operation.getContext().authRetryAttempted) {
          // Already retried once with a fresh token - let the error surface.
          return;
        }
        console.warn('Authentication error detected, attempting token refresh and retry');

        // Returning the Observable is what makes Apollo actually wait for and
        // replay the operation; discarding it silently drops the retry.
        return retryWithFreshToken(operation, forward);
      }
    }
  }

  if (networkError) {
    debugLog(`[Network error]: ${networkError}`);
    
    // Check for 401/403 status codes
    if ('statusCode' in networkError && 
        (networkError.statusCode === 401 || networkError.statusCode === 403)) {
      if (operation.getContext().authRetryAttempted) {
        return;
      }
      console.warn('Authentication error detected, attempting token refresh and retry');

      return retryWithFreshToken(operation, forward);
    }
  }
});

// Industry standard: Automatic retry with fresh token.
// Resolves to a fresh ID token, or null when the session cannot be recovered.
const fetchFreshToken = async (): Promise<string | null> => {
  try {
    const { auth } = await import('./firebaseConfig');
    const user = auth.currentUser;
    
    if (!user) {
      // No user logged in, trigger session expiry
      window.dispatchEvent(new CustomEvent('session-expired', {
        detail: { source: 'retry-failed', reason: 'no-user' }
      }));
      return null;
    }
    
    // Force refresh the token
    const freshToken = await user.getIdToken(true);
    
    if (!freshToken) {
      throw new Error('Failed to get fresh token');
    }
    
    // Log successful token refresh
    authMonitoringService.logTokenRefresh(user.email || 'unknown', true, 'apollo_retry');
    
    return freshToken;

  } catch (error) {
    console.error('Token refresh failed:', error);
    
    // Log token refresh failure
    authMonitoringService.logTokenRefresh('unknown', false, 'apollo_retry_failed');
    
    // Log session expiry
    authMonitoringService.logSessionExpiry('token_refresh_failed', 'apollo_client');
    
    // Token refresh failed, user needs to re-authenticate
    window.dispatchEvent(new CustomEvent('session-expired', {
      detail: { source: 'retry-failed', error }
    }));
    return null;
  }
};

/**
 * Replays a failed operation once with a freshly minted ID token. The returned
 * Observable is handed back to Apollo from the error link so the caller's
 * promise resolves with the retried result instead of the original auth error.
 */
const retryWithFreshToken = (operation: any, forward: any): Observable<FetchResult> =>
  new Observable<FetchResult>((observer) => {
    let subscription: { unsubscribe: () => void } | undefined;
    let cancelled = false;

    fetchFreshToken()
      .then((freshToken) => {
        if (cancelled) return;

        if (!freshToken) {
          observer.error(new Error('Session expired. Please sign in again.'));
          return;
        }

        operation.setContext(({ headers = {} }: any) => ({
          headers: {
            ...headers,
            authorization: `Bearer ${freshToken}`,
          },
          authRetryAttempted: true,
        }));

        debugLog('Token refreshed, retrying operation');
        subscription = forward(operation).subscribe({
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        });
      })
      .catch((error) => {
        if (!cancelled) observer.error(error);
      });

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
    };
  });

export const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'no-cache',
      // 'ignore' made useQuery screens render empty states on backend errors
      // with no indication anything went wrong.
      errorPolicy: 'none',
    },
    query: {
      fetchPolicy: 'no-cache',
      // 'all' resolved successfully with data: null on GraphQL errors, so
      // callers crashed on `data.someField` instead of surfacing the real
      // message. 'none' rejects, letting each caller's catch report it.
      errorPolicy: 'none',
    },
  },
});
